import type { MeasurementScale } from "./measurement";
import type { OverlayRegionMapping } from "./overlay";

// Feature source to control sync behavior in TakeoffsContext
// - "takeoff": Default, full sync enabled (used by main takeoff canvas)
// - "revision": Revision comparison modal, sync disabled (uses different API for saving)
export type FeatureSource = "takeoff" | "revision";

// Health/Ready endpoint types
export type TakeoffHandler = {
  trade: string;
  scope: string;
};

export type TakeoffHandlersCheck = {
  status: string;
  count: number;
  handlers: TakeoffHandler[];
  latency_ms: number;
};

export type TakeoffHealthReadyResponse = {
  status: string;
  checks: {
    takeoff_handlers: TakeoffHandlersCheck;
  };
};

export type EntityCoordinate = {
  entity_name: string;
  coordinates: [number, number]; // [x, y] coordinates
  confidence: number;
  source_pdf: string;
  page_number: number;
};

export type CoordinatesResponse = {
  success: boolean;
  entity_coordinates: EntityCoordinate[];
  entities_requested: number;
  entities_found: number;
  success_rate: number;
  processed_pdfs: string[];
  excluded_pdfs: string[];
  processing_time_seconds: number;
  error_message?: string | null;
};

export type SelectedRowFromTo = {
  from: string;
  to: string;
} | null;

export type MeasurementChangeCallback = (
  feeder: Feeder,
  newLength: number,
  originalLength: number,
) => void;

export type PdfCanvasRef = {
  drawDetectionAreasOnPdf: (
    detectionAreas: DetectionArea[],
    pageNumber?: number,
    feeders?: Feeder[],
  ) => void;
};

export type TakeoffType =
  | "linear"
  | "count"
  | "area"
  | "surface_area"
  | "volume_2d"
  | "volume_3d";

export type TakeoffGenerationJobSessionData = {
  job_id: string;
  status: string;
  message: string;
  timestamp: string;
  deliverable_name: string;
  selected_trade?: string;
  coordinates_response?: CoordinatesResponse;
};

export type TakeoffGenerationJobResultData = {
  job_id: string;
  status: string;
  upload_summary: {
    total_files: number;
    total_size_mb: number;
    file_types: string[];
    filenames: string[];
  };
  processed_documents: {
    filename: string;
    file_size_mb: number;
    pages_count: number;
    processing_time_seconds: number;
    feeders_found: number;
    upload_timestamp: string;
  }[];
  feeders_extracted: number;
  feeders: {
    source_equipment: string;
    destination_equipment: string;
    conduit_length_ft: number;
    wire_length_ft: number;
    termination_count: number;
    drawing_source: string;
    confidence_score: number;
    reasoning: string;
  }[];
  total_runtime_seconds: number;
  performance_summary: {
    total_runtime_seconds: number;
    pdf_processing_seconds: number;
    equipment_detection_seconds: number;
    connection_tracing_seconds: number;
    feeder_extraction_seconds: number;
    files_processed: number;
    pages_analyzed: number;
  };
  download_links: {
    csv: string | null;
    csv_with_reasoning: string | null;
    json_format: string | null;
    excel: string | null;
  };
};

export type MeasurementNodeType =
  | "countMeasurementNode"
  | "polylineNode"
  | "polygonNode";

// Base 2D coordinate type used throughout the application
// Extended to support Bezier curves for polylines, polygons, and cutouts
export type Point2D = {
  x: number;
  y: number;

  // Bezier control handle for the curve LEAVING this point (toward the next point)
  // If undefined, the segment leaving this point is a straight line
  handleOut?: { x: number; y: number };

  // Bezier control handle for the curve ENTERING this point (from the previous point)
  // If undefined, the segment entering this point is a straight line
  handleIn?: { x: number; y: number };
};

export type TakeoffPdfDownloadPayloadMeasurement = {
  id: string;
  category: string;
  name: string;
  points: Point2D[];
  page_number: number;
  color: number[];
  line_width?: number;
  start_tip?: string;
  end_tip?: string;
  shape?: string;
  size?: number;
};
export type TakeoffPdfDownloadPayload = {
  s3_key: string;
  output_filename: string;
  measurements: TakeoffPdfDownloadPayloadMeasurement[];
};

export type TakeoffPdfDownloadData = {
  status: string;
  message: string;
  s3_url: string;
};

// S3 URL and key pair from the API
export type S3URLNKey = {
  url: string;
  key: string;
  expired_at?: string;
};

/**
 * Queue status information for takeoff processing.
 * Provides visibility into the takeoff service's processing queue.
 */
export type TakeoffQueueStatus = {
  /** Name of the processing queue */
  queue_name?: string;
  /** Current position in the queue (1-indexed, null if not queued) */
  position?: number | null;
  /** Total items in the queue */
  total?: number | null;
  /** Number of units waiting to be processed */
  enqueued_units?: number | null;
  /** Number of units completed */
  done_units?: number | null;
  /** Number of units currently being processed */
  running_units?: number | null;
  /** Number of units that failed processing */
  failed_units?: number | null;
};

export type PageContentType = {
  classification: "raster" | "vector" | "mixed";
  raster_coverage: number;
  has_vector_content: boolean;
  has_searchable_text: boolean;
  searchable_text_length: number;
};

// RawTakeoffRecord from /api/takeoffs/{job_id} endpoint (process/v2 workflow)
export type RawTakeoffRecord = {
  id: string;
  job_id: string;
  pages_s3_link: S3URLNKey;
  webp_s3_link?: S3URLNKey | null;
  json_s3_link: S3URLNKey | null;
  // Per-result-type presigned links (ENG-18247). Keyed by result_type
  // ("electrical_sld", "label", "landscape", "electrical"). Optional for
  // pre-deploy back-compat; resolve via `resolveResultLink`.
  results_by_type?: Record<string, S3URLNKey>;
  title: string;
  code: string;
  source_pdf: string;
  scale?: string;
  revision_number?: string | null;
  sheet_number?: string | null;
  scope?: string;
  trade?: string;
  status?: string;
  progress?: number; // 0-1 range (e.g., 0.5 = 50%)
  queue?: TakeoffQueueStatus;
  page_content_type?: PageContentType | null;
};

export type TakeoffAutoExtractionSummary = {
  status: string;
  decision: string;
  error?: string | null;
  class_counts?: Record<string, number>;
  table_result?: { key: string; url: string; expired_at?: string } | null;
  detail_result?: { key: string; url: string; expired_at?: string } | null;
  finish_plan_result?: { key: string; url: string; expired_at?: string } | null;
};

export type TakeoffRecord = {
  id: string;
  external_id?: string;
  job_id: string;
  pages_s3_link: S3URLNKey;
  webp_s3_link?: S3URLNKey | null;
  json_s3_link: S3URLNKey | null;
  json_s3_links?: S3URLNKey[];
  // Per-result-type presigned links (ENG-18247); resolve via `resolveResultLink`.
  results_by_type?: Record<string, S3URLNKey>;
  title: string;
  code: string;
  source_pdf: string;
  scale?: MeasurementScale | null;
  revision_number?: string | null;
  sheet_number?: string | null;
  scope?: string;
  scopes?: string[];
  trade?: string;
  discipline_designator?: string;
  status?: string;
  file_url?: string;
  gcs_url?: string;
  progress?: number; // 0-1 range (e.g., 0.5 = 50%)
  queue?: TakeoffQueueStatus;
  selected_areas?: TakeoffSelectedArea[];
  page_content_type?: PageContentType | null;
  auto_extraction?: TakeoffAutoExtractionSummary | null;
};

export type NeedsAttentionReason =
  | "scale"
  | "feeder-points"
  | "dimension"
  | "rise-drop"  // Electrical runs (branch-circuits, home-run, feeders) missing rise/drop dimension
  | null;

export type TakeoffGenerationFileDetail = {
  pageId?: number; // Rails database ID (page.id) - used for indexing/lookups
  pageCode: string; // Human-readable page code (page.code) - used for display
  type: string;
  fileUrl?: string;
  fileName: string;
  rawData?: TakeoffRecord | null;
  needsAttentionReason?: NeedsAttentionReason;
  rotation?: number;
  completedAt?: string | null;
};
export type TakeoffGenerationsFilesDetail = TakeoffGenerationFileDetail[];

// Multiple Takeoff API Types
export type TakeoffScopeError = {
  scope: string;
  error: string;
};

export type GenerateTakeoffMultipleScopesResponse = {
  takeoff_id: string;
  job_id: string;
  status: string;
  trade: string;
  scopes: string[];
  message: string;
  pages_s3_link: S3URLNKey | null;
  webp_s3_link?: S3URLNKey | null;
  json_s3_link: S3URLNKey | null;
  json_s3_links: S3URLNKey[];
  // Per-result-type presigned links (ENG-18247); resolve via `resolveResultLink`.
  results_by_type?: Record<string, S3URLNKey>;
  missing_scopes: TakeoffScopeError[];
  title: string;
  code: string;
  scale: string;
  discipline_designator: string;
  source_pdf: string;
};

export type TakeoffDataMultipleScopePayload = {
  takeoff_id: string;
};

export type TakeoffDataMultipleScopeResponse = {
  id: string;
  job_id: string;
  pages_s3_link: S3URLNKey;
  webp_s3_link?: S3URLNKey | null;
  json_s3_link: S3URLNKey | null;
  json_s3_links: S3URLNKey[];
  // Per-result-type presigned links (ENG-18247). Keyed by result_type
  // ("electrical_sld", "label", "landscape", "electrical"). Optional for
  // pre-deploy back-compat; resolve via `resolveResultLink`.
  results_by_type?: Record<string, S3URLNKey>;
  title: string;
  code: string;
  scale: string;
  trade: string;
  scope: string;
  scopes: string[];
  discipline_designator: string;
  source_pdf: string;
  status?: string;
  progress?: number; // 0-1 range (e.g., 0.5 = 50%)
  message?: string;
  queue?: TakeoffQueueStatus;
  selected_areas?: TakeoffSelectedArea[];
};

/**
 * Table data structure for schedule-based takeoffs.
 * Represents extracted tabular data from schedule pages.
 */
export type TakeoffTableData = {
  /** Column headers from the schedule table */
  headers: string[];
  /** Row data as arrays of strings (each row matches headers length) */
  rows: string[][];
  /** Header name of the identifier column (set by "Set Identifier" in schedule editor). Used by lighting-fixtures. */
  identifier_column?: string;
};

/**
 * Custom payload options for per-page takeoff API calls.
 * Used when generating takeoffs with additional context beyond trade/scopes.
 */
export type TakeoffCustomPayload = {
  /** Table data extracted from schedule pages */
  table_data?: TakeoffTableData[];
  /** Override scale for this specific takeoff */
  scale_override?: string;
  /** Drawing scale ratio (e.g. 96 for 1/8"=1'-0"). Used by mechanical/hvac ductwork. */
  scale_ratio?: number;
  /** Skip cache and force regeneration */
  skip_cache?: boolean;
  /** Enables the agentic completion and review frontier for HVAC ductwork. */
  agentic_duct_frontier_enabled?: boolean;
  /**
   * Enables the AI network reviewer for 1D piping. When true, CP writes the
   * `network_review` bootstrap block into the piping result so the FE can
   * surface the reviewer (mirrors `agentic_duct_frontier_enabled` for ductwork).
   */
  network_review_enabled?: boolean;
  /**
   * When true, CP expands the AI's label-search list to include leading-word
   * prefix tokens from concatenated MARKs and uses a word-position-fallback
   * matcher in the BE enrichment. Wired off the boon-core
   * `:expand_schedule_join` Flipper flag. Default: false (CP uses strict-exact).
   */
  expand_schedule_join?: boolean;
  /**
   * V1 parity: routes the BE through a newer model variant. Independent of
   * `skip_cache`. Omitted entirely when false so the BE contract stays clean.
   */
  use_new_model?: boolean;
  /**
   * Room-keyed detail extractions forwarded so the agentic chain can filter
   * detail spec extractions per room type. Built per-page by
   * `injectDetailExtractions` (takeoff-v2). Rides ALONGSIDE `extraction_dpi`.
   */
  detail_extractions?: Record<
    string,
    Array<{
      name: string;
      description: string;
      bbox_full_page?: [number, number, number, number];
    }>
  >;
  /**
   * Companion DPI for `detail_extractions` bboxes — the BE uses it to convert
   * pixel-space bboxes to PDF points. Defaults to V1's 200 when omitted.
   */
  extraction_dpi?: number;
  /** Legend swatches grouped by source page for texture-based area matching */
  legends?: Array<{
    takeoff_id: string;
    type: string;
    items: Array<{
      label: string;
      left: number;
      top: number;
      width: number;
      height: number;
    }>;
  }>;
  /**
   * Scope-specific extras merged into the payload by per-scope flows: the
   * wall-types template detection path (`template_bbox`, `template_dpi`,
   * `sensitivity`, `callout_detection_method`) and the home-run routing
   * fallback (`resolved_regex`). V1 sends these flat inside `payload`, so V2
   * mirrors the shape verbatim. Index signature kept loose since the set is
   * open-ended per scope.
   */
  [scopeExtra: string]: unknown;
};

export type GenerateBulkTakeoffRecord = {
  takeoff_id: string;
  trade: string;
  scopes: string[];
  /** Top-level selected areas for result clipping (matches backend SelectedArea model) */
  selected_areas?: TakeoffSelectedArea[];
  /** Overlay region mappings for source→destination bbox remapping */
  mapped_overlay_regions?: OverlayRegionMapping[];
  /**
   * Drawing scale ratio (e.g. 96 for 1/8"=1'-0"). Top-level, per record —
   * forwarded into the per-page processing path, same effect as the manual
   * endpoint's top-level `scale_ratio`. Required for mechanical/hvac ductwork.
   * (ENG-12421 wired `/bulk` to honor this.)
   */
  scale_ratio?: number;
  /**
   * Optional custom payload for per-page API calls.
   * When provided, the hook uses `generateTakeoffDataMultipleScopes` per page
   * instead of the bulk API. Supports table_data for schedule-based takeoffs.
   */
  payload?: TakeoffCustomPayload;
};

export type GenerateBulkTakeoffPayload = {
  takeoff_records: GenerateBulkTakeoffRecord[];
};

/**
 * One per-record entry in the `/api/v4/takeoff/bulk` response (the gateway's
 * `BulkTakeoffRecordStatus`): `takeoff_id` echoes the input id, `job_id` is the
 * TOP-LEVEL shared gateway job spanning all records, and per-record failures
 * surface in `error_detail`. The legacy `data`/`error` fields are kept optional
 * for backward compatibility with existing consumers but are not part of the
 * current wire shape.
 */
export type GenerateBulkTakeoffRecordStatus = {
  takeoff_generation_id?: string;
  takeoff_generation_order?: number;
  takeoff_id: string;
  job_id?: string | null;
  status: string;
  message?: string;
  trade?: string | null;
  scopes?: string[];
  error_detail?: string | null;
  // `data`/`error` are not part of the current gateway contract, but V1's
  // `bundles/projects/hooks/takeoff/useBulkTakeoff.ts` still reads them — keep
  // them optional until that V1 consumer is retired, then remove.
  /** @deprecated not emitted by the current gateway contract (V1 useBulkTakeoff still reads it) */
  data?: TakeoffDataMultipleScopeResponse;
  /** @deprecated superseded by `error_detail` (V1 useBulkTakeoff still reads it) */
  error?: string;
};

export type GenerateBulkTakeoffResponse = {
  /** Overall run status: "queued" | "partial_failure" | "failed". */
  status?: string;
  message?: string;
  results: GenerateBulkTakeoffRecordStatus[];
};

export type DetectionArea = {
  from_bbox?: [number, number, number, number];
  to_bbox?: [number, number, number, number];
  connection_line?: [number, number, number, number];
  confidence: number;
  from_location: Point2D;
  to_location: Point2D;
};

export type Feeder = {
  from?: string;
  to?: string;
  source_equipment?: string;
  destination_equipment?: string;
  runs?: number;
  voltage?: string;
  ampacity?: string;
  conductor_type?: string;
  electrical_confidence?: number;
  extraction_method?: string;
  detection_area?: DetectionArea;
  length?: number;
  conduit_length_ft?: number;
  wire_length_ft?: number;
  termination_count?: number;
  measurement_source?: string;
  measurement_method?: string;
  measurement_confidence?: number;
  routing_notes?: string;
  integration_timestamp?: number;
  data_completeness?: string;
  overall_confidence?: number;
  brad_methodology_applied?: boolean;
  calculation_confidence?: number;
  measurement_verified?: boolean;
};

export type FeederRecordCSVFormat = {
  Drawing: string;
  Description: string;
  Measure: number;
  Count: number;
  Reasoning: string;
};

export type SpatialDetection = {
  feeder: string;
  detection_area: DetectionArea;
  source_drawing: string;
};

export type SystemInfo = {
  architecture: string;
  phase1_method: string;
  phase2_method: string;
  phase3_method: string;
};

export type ExtractionSummary = {
  total_feeders: number;
  extracted_at: string;
  pipeline_type: string;
  extraction_method: string;
};

// Construction/Structural trade types
export type ConstructionBeamsMeta = {
  input_pdf: string;
  created_at: string;
  page_count: number;
  coordinate_system: string;
  units: string;
  version: string;
};

export type BoundingBox = {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
};

export type BeamConnectionData = {
  category: string;
  confidence: number;
  center_pt: [number, number];
  bbox_pt: [number, number, number, number];
};

export type StructuralOccurrenceLineSegment = {
  start_pt: [number, number];
  end_pt: [number, number];
  center_pt: [number, number];
  length_pt: number;
  angle_deg: number;
  distance_to_text_pt: number;
  angle_difference_deg: number;
  start_connection?: BeamConnectionData | null;
  end_connection?: BeamConnectionData | null;
};

export type StructuralOccurrence = {
  page_index: number;
  page_number: number;
  page_width_pt: number;
  page_height_pt: number;
  bbox_pt: BoundingBox;
  bbox_norm: BoundingBox;
  rotation_applied?: boolean;
  stud_text?: string;
  stud_sum?: number;
  line_segment?: StructuralOccurrenceLineSegment;
};

export type StructuralElement = {
  piece: string;
  category: string;
  color_rgb: [number, number, number];
  qty: number;
  estimated_length_ft: number;
  estimated_weight_per_ft_lb: number;
  estimated_total_length_ft: number;
  estimated_total_weight_lb: number;
  stud_sum?: number;
  occurrences: StructuralOccurrence[];
};

export type ConstructionBeamsJsonData = {
  meta: ConstructionBeamsMeta;
  elements: StructuralElement[];
  columns?: BaseTableColumnsData;
};

// Generic base type for trade data with common structure
// This pattern is used for trades that follow the standard result array format
// Note: result can be either TResult[] (v2 API) or the direct JSON data (v3 API)
export type BaseTradeData<TResult, TJsonData = any> = {
  job_id: string;
  status: string;
  scope: string;
  trade: string;
  takeoff_page_id?: string;
  result: TResult[] | TJsonData;
  uploaded_files?: string[];
  upload_summary?: {
    processed_files: number;
  };
};

// Generic base type for result objects with common structure
// This pattern is used for trade results that follow the standard pdf/output_path/json format
export type BaseResult<TJsonData> = {
  pdf: string;
  output_path: string;
  json: TJsonData;
  error_message?: string | null;
};

export type BaseTableColumnsDefinition = {
  name: string;
  key: string;
  unit: string | null;
  formula?: string; // Custom formula for calculated columns (e.g., tonnage)
  readOnly?: boolean; // Derived metadata edited through its validated workflow
  options?: string[]; // Predefined options for select/dropdown columns
};
export type BaseTableColumnsData = BaseTableColumnsDefinition[];

// Construction beams specific trade data
export type ConstructionBeamsTradeData = BaseTradeData<
  ConstructionBeamsResult,
  ConstructionBeamsJsonData
>;

// Construction beams specific result
export type ConstructionBeamsResult = BaseResult<ConstructionBeamsJsonData>;

// Plumbing pipe trade types
export type PlumbingPipeCoordinates = {
  start_x: number;
  start_y: number;
  end_x: number;
  end_y: number;
};

export type PlumbingPipeProperties = {
  length: number;
  thickness: number;
  color: [number, number, number];
  orientation: "horizontal" | "vertical";
  pipe_type: string;
};

export type PlumbingPipeLineSegment = {
  uuid: string;
  coordinates: PlumbingPipeCoordinates;
  properties: PlumbingPipeProperties;
};

export type PlumbingPipeTextCoordinates = {
  mid_x: number;
  mid_y: number;
  bbox: [number, number, number, number];
};

export type PlumbingPipeVectorInfo = {
  vector_id: string;
  distance: number;
  properties: {
    thickness: number;
    color: [number, number, number];
  };
};

export type PlumbingPipeTextInstance = {
  uuid: string;
  full_text: string;
  coordinates: PlumbingPipeTextCoordinates;
  page_number: number;
  pipe_type: string;
  target_thickness: number;
  has_vector: boolean;
  vector_info: PlumbingPipeVectorInfo | null;
};

export type PlumbingPipeNetworkStats = {
  total_text_instances: number;
  total_line_segments: number;
  vectors_found: number;
};

export type PlumbingPipeNetwork = {
  uuid: string;
  pipe_type: string;
  target_thickness: number;
  text_instances: PlumbingPipeTextInstance[];
  line_segments: PlumbingPipeLineSegment[];
  stats: PlumbingPipeNetworkStats;
};

export type PlumbingPipeMetadata = {
  pipe_types_processed: string[];
  page_numbers: number[];
  filters_applied: {
    thickness_tolerance: number;
    min_line_length: number;
    coordinate_tolerance: number;
    straight_lines_only: boolean;
    black_lines_only: boolean;
    pipe_types: string[];
  };
  text_found: boolean;
  analysis_stats: {
    total_text_instances: number;
    vectors_found: number;
    pipe_type_stats: Record<
      string,
      {
        text_instances: number;
        vectors_found: number;
        network_lines: number;
        target_thickness: number;
      }
    >;
  };
  total_pipe_types_found: number;
  overall_stats: {
    total_text_instances: number;
    total_line_segments: number;
    total_vectors_found: number;
  };
};

export type PlumbingPipeJsonData = {
  uuid: string;
  pipe_networks: Record<string, PlumbingPipeNetwork>;
  metadata: PlumbingPipeMetadata;
};

// Plumbing pipe specific trade data
export type PlumbingPipeTradeData = BaseTradeData<
  PlumbingPipeResult,
  PlumbingPipeJsonData
>;

// Plumbing pipe specific result
export type PlumbingPipeResult = BaseResult<PlumbingPipeJsonData>;

// Base types for coordinate elements and groups (DRY principle)
export type BaseCoordinateElement = {
  type: string;
  color: [number, number, number];
  points: Point2D[];
  confidence?: number;
};

export type ElementGroup<
  T extends BaseCoordinateElement = BaseCoordinateElement,
> = {
  group: string;
  group_key?: DuctworkGroupKey; // Optional structured key for filtering/sorting
  count?: number;
  elements: T[];
};

// Ductwork-specific types for enhanced grouping
export type DuctworkGroupKey = {
  shape: "circular" | "rectangular" | "unassigned";
  size: string; // "14" for circular, "18x18" for rectangular
  system: string | null; // "supply" | "return" | "exhaust" | null
  system_type?: string | null; // Backend v2.0 sends system_type instead of system
  material: string | null; // "galvanized_steel" | "fiberglass" | etc.
};

export type DuctworkElement = BaseCoordinateElement & {
  type: "line";
  length_points: number;
  label: string;
  size: string;
  system: string | null; // For future use
  material: string | null; // For future use
};

export type DuctworkElementGroup = ElementGroup<DuctworkElement> & {
  group_key: DuctworkGroupKey;
  count: number;
};

// Construction concrete specific types
export type ConcreteScaleInfo = {
  display: string;
  feet_per_inch: number;
  scale_ratio: number;
  location: string;
  detection_method: string;
  raw_text: string;
};

export type ConcreteAreaAnalysis = {
  pixel_area: number;
  scale_info: ConcreteScaleInfo;
  area_square_feet: number;
  area_square_meters: number;
  calculation_method: string;
  dpi_used: number;
  scale_display: string;
  scaled_to_source: boolean;
  coordinate_system: string;
};

export type ConcreteQualityInfo = {
  attempts_used: number;
  fallback_used: boolean;
  confidence_level: string;
};

export type ConstructionConcreteJsonData = {
  status: string;
  input_type: string;
  coordinate: ElementGroup[];
  area_analysis: ConcreteAreaAnalysis;
  bounding_box: BoundingBox;
  processing_time_seconds: number;
  pipeline_version: string;
  quality_info: ConcreteQualityInfo;
};

// Construction concrete specific trade data
export type ConstructionConcreteTradeData = BaseTradeData<
  ConstructionConcreteResult,
  ConstructionConcreteJsonData
>;

// Construction concrete specific result
export type ConstructionConcreteResult =
  BaseResult<ConstructionConcreteJsonData>;

// Mechanical pipe trade types
export type MechanicalPipeMetadata = {
  analysis_type: string;
  pdf_source: string;
  processing_parameters: {
    precision: number;
    include_estimates: boolean;
  };
  pages_analyzed: number[];
  multi_page_analysis: boolean;
  timestamp: string;
};

export type MechanicalPipeSummary = {
  total_centerlines: number;
  pipe_systems_found: string[];
  systems_count: number;
};

export type MechanicalPipeJsonData = {
  metadata: MechanicalPipeMetadata;
  summary: MechanicalPipeSummary;
  coordinate: ElementGroup[];
};

// Mechanical pipe specific trade data
export type MechanicalPipeTradeData = BaseTradeData<
  MechanicalPipeResult,
  MechanicalPipeJsonData
>;

// Mechanical pipe specific result
export type MechanicalPipeResult = BaseResult<MechanicalPipeJsonData>;

// Mechanical ductwork trade types
export type MechanicalDuctworkMetadata = {
  analysis_type: string;
  pdf_source: string;
  timestamp: string;
};

export type MechanicalDuctworkJsonData = {
  status: string;
  version: string;
  input_type: string;
  metadata: MechanicalDuctworkMetadata;
  summary: Record<string, number>;
  coordinate: ElementGroup[];
};

// Mechanical ductwork specific trade data
export type MechanicalDuctworkTradeData = BaseTradeData<
  MechanicalDuctworkResult,
  MechanicalDuctworkJsonData
>;

// Mechanical ductwork specific result (includes optional annotated_pdf field)
export type MechanicalDuctworkResult = BaseResult<MechanicalDuctworkJsonData>;

// Plumbing fixtures trade types
export type PlumbingFixturePage = {
  page_number: number;
  total_detections: number;
  fixtures_by_type: Record<string, number>;
  coordinate: ElementGroup[];
};

export type PlumbingFixtureJsonData = {
  success: boolean;
  document_name: string;
  total_pages: number;
  total_detections: number;
  pages: PlumbingFixturePage[];
};

// Plumbing fixtures specific trade data
export type PlumbingFixtureTradeData = BaseTradeData<
  PlumbingFixtureResult,
  PlumbingFixtureJsonData
>;

// Plumbing fixtures specific result
export type PlumbingFixtureResult = BaseResult<PlumbingFixtureJsonData>;

// Landscape specific trade data (count mode)
export type LandscapeCountTradeData = {
  job_id: string;
  takeoff_page_id: string;
  status: string;
  upload_summary: {
    processed_files: number;
    measurements_found: number;
  };
  uploaded_files: any[];
  scope: "landscape";
  trade: "landscape-trade-this-hit-from-outside";
  result: {
    job_id: string;
    success: boolean;
    measurements: LandscapeCountMeasurement[];
    quantity_mode: "count";
    quantity_result: number;
    analysis: {
      raw_detections: number;
      final_count: number;
    };
    processing_metadata: any;
    debug_image_path: string | null;
  };
  processing_metadata: any;
  error_message: string | null;
};

// Landscape area trade data (area mode)
export type LandscapeAreaTradeData = {
  job_id: string;
  takeoff_page_id: string;
  status: string;
  upload_summary: {
    processed_files: number;
    measurements_found: number;
  };
  uploaded_files: any[];
  scope: "landscape";
  trade: "landscape-trade-this-hit-from-outside";
  result: {
    job_id: string;
    success: boolean;
    measurements: LandscapeAreaMeasurement[];
    quantity_mode: "area";
    quantity_result: number;
    analysis: {
      union_area_pixels: number;
      contour_area_pixels: number;
      individual_boxes_total_pixels: number;
      overlap_reduction_percentage: number;
      area_square_points: number | null;
      area_square_feet: number | null;
      scale_factor_used: any;
    };
    contours: string[];
    processing_metadata: any;
    debug_image_path: string | null;
  };
  processing_metadata: any;
  error_message: string | null;
};

// Union type for both modes
export type LandscapeTradeData =
  | LandscapeCountTradeData
  | LandscapeAreaTradeData;

// Landscape count measurement type
export type LandscapeCountMeasurement = {
  id: string;
  category: string;
  name: string;
  quantity: number;
  unit: string;
  center_x: number;
  center_y: number;
  confidence_score: number;
  source: string;
};

// Landscape area measurement type
export type LandscapeAreaMeasurement = {
  id: string;
  category: string;
  name: string;
  quantity: number;
  unit: string;
  real_world_area_sqft: number | null;
  confidence_score: number;
  source: string;
};

// Construction wall centerline trade types
export type WallCenterlineStatistics = {
  total_centerlines: number;
  horizontal: number;
  vertical: number;
  diagonal: number;
};

export type WallCenterlineProcessingMetadata = {
  method: string;
  coordinate_system: string;
};

export type ConstructionWallCenterlineJsonData = {
  status: string;
  version: string;
  input_type: string;
  coordinate: ElementGroup[];
  statistics: WallCenterlineStatistics;
  building_bbox: [number, number, number, number];
  processing_metadata: WallCenterlineProcessingMetadata;
};

// Construction wall centerline specific trade data
export type ConstructionWallCenterlineTradeData = BaseTradeData<
  ConstructionWallCenterlineResult,
  ConstructionWallCenterlineJsonData
>;

// Construction wall centerline specific result
export type ConstructionWallCenterlineResult =
  BaseResult<ConstructionWallCenterlineJsonData>;

// Steel columns trade types
export type SteelColumnsJsonData = {
  coordinate: ElementGroup[];
};

// Steel columns specific trade data
export type SteelColumnsTradeData = BaseTradeData<
  SteelColumnsResult,
  SteelColumnsJsonData
>;

// Steel columns specific result
export type SteelColumnsResult = BaseResult<SteelColumnsJsonData>;

// Steel columns (framing plan) trade types — RF-DETR detector emits a
// `coordinate[0].categories[].elements[]` envelope (one categories[] entry
// per detected column mark, e.g. "C12", "HSS6x6x3/8"). The 5 RF-DETR
// subclasses are collapsed to a single bucket at decode time so the
// downstream contract stays a flat per-mark list of count detections.
// Each element carries `mark` / `label` (when the VLM/PaddleOCR/PyMuPDF
// fallback chain assigned one), `bbox`, and a single center `points[0]`.
export type SteelColumnsFramingPlanElement = {
  type: "counting";
  confidence: number;
  category: string;
  points: { x: number; y: number }[];
  bbox?: [number, number, number, number];
  mark?: string;
  label?: string;
  color?: number[];
};

export type SteelColumnsFramingPlanCategory = {
  group: string;
  elements: SteelColumnsFramingPlanElement[];
};

export type SteelColumnsFramingPlanCoordinate = {
  categories: SteelColumnsFramingPlanCategory[];
};

export type SteelColumnsFramingPlanJsonData = {
  coordinate: SteelColumnsFramingPlanCoordinate[];
};

export type SteelColumnsFramingPlanTradeData = BaseTradeData<
  SteelColumnsFramingPlanResult,
  SteelColumnsFramingPlanJsonData
>;

export type SteelColumnsFramingPlanResult =
  BaseResult<SteelColumnsFramingPlanJsonData>;

// Civil utility trade types
export type CivilUtilityJsonData = {
  coordinate: ElementGroup[];
};

// Civil utility specific trade data
export type CivilUtilityTradeData = BaseTradeData<
  CivilUtilityResult,
  CivilUtilityJsonData
>;

// Civil utility specific result
export type CivilUtilityResult = BaseResult<CivilUtilityJsonData>;

// Architectural/Finishes - Walls specific trade data
// This one has the exact same schema as construction wall centerline, but the trade and scope are different
// Reusing the same result type
export type ArchitecturalWallsTradeData = BaseTradeData<
  ConstructionWallCenterlineResult,
  ConstructionWallCenterlineJsonData
>;

// Electrical trade data (existing structure - uses custom format, not BaseTradeData)
export type ElectricalTradeData = {
  extraction_summary: ExtractionSummary;
  feeders: Feeder[];
  feeder_records_csv_format: FeederRecordCSVFormat[];
  spatial_detections: SpatialDetection[];
  system_info: SystemInfo;
  status?: string;
  scope?: string | null;
  trade?: string | null;
};

// Electrical receptacles/outlets trade types
export type ElectricalReceptaclesSummary = {
  total_detections: number;
  detections_by_symbol: Record<string, number>;
  processing_time_seconds: number;
};

export type ElectricalReceptaclesMetadata = {
  model_id: string;
  image_width: number;
  image_height: number;
  grid_size: [number, number];
};

export type ElectricalReceptaclesJsonData = {
  status: string;
  version: string;
  coordinate: ElementGroup[];
  summary: ElectricalReceptaclesSummary;
  metadata: ElectricalReceptaclesMetadata;
};

// Electrical receptacles specific trade data
export type ElectricalReceptaclesTradeData = BaseTradeData<
  ElectricalReceptaclesResult,
  ElectricalReceptaclesJsonData
>;

// Electrical receptacles specific result
export type ElectricalReceptaclesResult =
  BaseResult<ElectricalReceptaclesJsonData>;

// Electrical switches & dimmers trade types
// Same schema as receptacles (both scopes consume the `electrical-symbols`
// Detectron2 detector and emit `coordinate[].elements[]` with type "counting"
// + points). Kept as a separate type family so the tradeNodeManager renderer
// and the BE→FE dispatcher can discriminate on scope.
export type ElectricalSwitchesDimmersSummary = {
  total_detections: number;
  detections_by_symbol: Record<string, number>;
  processing_time_seconds: number;
};

export type ElectricalSwitchesDimmersMetadata = {
  model_id: string;
  image_width: number;
  image_height: number;
  grid_size: [number, number];
};

export type ElectricalSwitchesDimmersJsonData = {
  status: string;
  version: string;
  coordinate: ElementGroup[];
  summary: ElectricalSwitchesDimmersSummary;
  metadata: ElectricalSwitchesDimmersMetadata;
};

export type ElectricalSwitchesDimmersResult =
  BaseResult<ElectricalSwitchesDimmersJsonData>;

export type ElectricalSwitchesDimmersTradeData = BaseTradeData<
  ElectricalSwitchesDimmersResult,
  ElectricalSwitchesDimmersJsonData
>;

// Union type for different trade data
export type TakeoffGenerationFileJsonData =
  | ElectricalTradeData
  | ElectricalReceptaclesTradeData
  | ElectricalSwitchesDimmersTradeData
  | ConstructionBeamsTradeData
  | ConstructionConcreteTradeData
  | ConstructionWallCenterlineTradeData
  | SteelColumnsTradeData
  | SteelColumnsFramingPlanTradeData
  | CivilUtilityTradeData
  | PlumbingPipeTradeData
  | MechanicalPipeTradeData
  | MechanicalDuctworkTradeData
  | PlumbingFixtureTradeData
  | LandscapeTradeData
  | ArchitecturalWallsTradeData;

// Type guards for discriminating between trade types
export function isElectricalTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is ElectricalTradeData {
  if (data === null) return false;
  return (
    // "extraction_summary" in data &&
    "feeders" in data && Array.isArray((data as ElectricalTradeData).feeders)
  );
}

export function isElectricalReceptaclesTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is ElectricalReceptaclesTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "trade" in data &&
    (data as ElectricalReceptaclesTradeData).trade === "electrical" &&
    "scope" in data &&
    (data as ElectricalReceptaclesTradeData).scope === "receptacles-outlets"
  );
}

export function isElectricalSwitchesDimmersTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is ElectricalSwitchesDimmersTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "trade" in data &&
    (data as ElectricalSwitchesDimmersTradeData).trade === "electrical" &&
    "scope" in data &&
    (data as ElectricalSwitchesDimmersTradeData).scope === "switches-dimmers"
  );
}

export function isConstructionBeamsTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is ConstructionBeamsTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "trade" in data &&
    (data as ConstructionBeamsTradeData).trade === "construction/structural" &&
    "scope" in data &&
    (data as ConstructionBeamsTradeData).scope === "steel-beams-joists"
  );
}

export function isConstructionConcreteTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is ConstructionConcreteTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "trade" in data &&
    (data as ConstructionConcreteTradeData).trade ===
      "construction/structural" &&
    "scope" in data &&
    (data as ConstructionConcreteTradeData).scope === "concrete-slabs"
  );
}

export function isConstructionWallCenterlineTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is ConstructionWallCenterlineTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "trade" in data &&
    (data as ConstructionWallCenterlineTradeData).trade ===
      "construction/structural" &&
    "scope" in data &&
    (data as ConstructionWallCenterlineTradeData).scope === "wall-centerline"
  );
}

export function isSteelColumnsTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is SteelColumnsTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "trade" in data &&
    (data as SteelColumnsTradeData).trade === "construction/structural" &&
    "scope" in data &&
    (data as SteelColumnsTradeData).scope === "columns"
  );
}

export function isSteelColumnsFramingPlanTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is SteelColumnsFramingPlanTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "trade" in data &&
    (data as SteelColumnsFramingPlanTradeData).trade ===
      "construction/structural" &&
    "scope" in data &&
    (data as SteelColumnsFramingPlanTradeData).scope ===
      "steel-columns-framing-plan"
  );
}

export function isCivilUtilityTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is CivilUtilityTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "trade" in data &&
    (data as CivilUtilityTradeData).trade === "civil" &&
    "scope" in data &&
    (data as CivilUtilityTradeData).scope === "utility"
  );
}

export function isPlumbingPipeTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is PlumbingPipeTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "scope" in data &&
    (data as PlumbingPipeTradeData).scope === "piping-runs-plumb"
  );
}

export function isMechanicalPipeTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is MechanicalPipeTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "trade" in data &&
    (data as MechanicalPipeTradeData).trade === "mechanical/hvac" &&
    "scope" in data &&
    ((data as MechanicalPipeTradeData).scope === "piping-runs-mech" ||
      (data as MechanicalPipeTradeData).scope === "piping-2d-line" ||
      // process-piping-runs emits the same `coordinate` envelope; render it through
      // the identical mechanical-pipe polyline path (transformer + renderer).
      (data as MechanicalPipeTradeData).scope === "process-piping-runs")
  );
}

export function isMechanicalDuctworkTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is MechanicalDuctworkTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "trade" in data &&
    (data as MechanicalDuctworkTradeData).trade === "mechanical/hvac" &&
    "scope" in data &&
    (data as MechanicalDuctworkTradeData).scope === "ductwork-runs"
  );
}

export function isPlumbingFixtureTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is PlumbingFixtureTradeData {
  if (data === null) return false;
  return (
    "result" in data &&
    "trade" in data &&
    (data as PlumbingFixtureTradeData).trade === "plumbing" &&
    "scope" in data &&
    (data as PlumbingFixtureTradeData).scope === "fixtures"
  );
}

export function isLandscapeTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is LandscapeTradeData {
  if (data === null) return false;
  // Landscape data has a unique structure: result is an object (not array) with measurements array inside
  // This is different from other trades where result is an array
  return (
    "result" in data &&
    typeof (data as LandscapeTradeData).result === "object" &&
    !Array.isArray((data as LandscapeTradeData).result) &&
    "measurements" in ((data as LandscapeTradeData).result || {}) &&
    Array.isArray((data as LandscapeTradeData).result?.measurements) &&
    "trade" in data &&
    (data as LandscapeTradeData).trade ===
      "landscape-trade-this-hit-from-outside" &&
    "scope" in data &&
    (data as LandscapeTradeData).scope === "landscape"
  );
}

export function isLandscapeAreaTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is LandscapeAreaTradeData {
  if (!isLandscapeTradeData(data)) return false;
  return (
    data.result.quantity_mode === "area" && "contours" in (data.result || {})
  );
}

export function isLandscapeCountTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is LandscapeCountTradeData {
  if (!isLandscapeTradeData(data)) return false;
  return data.result.quantity_mode === "count";
}

export function isArchitecturalWallsTradeData(
  data: TakeoffGenerationFileJsonData | null,
): data is ArchitecturalWallsTradeData {
  if (data === null) return false;
  const scope = (data as ArchitecturalWallsTradeData).scope;
  return (
    "result" in data &&
    "trade" in data &&
    (data as ArchitecturalWallsTradeData).trade === "architectural/finishes" &&
    "scope" in data &&
    (scope === "walls" || scope === "wall-types")
  );
}

// Schedule-based takeoff types (used for table extraction → takeoff flow)
export type ScheduleTableData = {
  pageId: number;
  tableIndex: number;
  externalId: string;
  table: {
    id?: number;
    project_page_id?: number;
    name?: string;
    headers: string[];
    column_ids?: number[];
    rows: string[][];
    row_ids?: number[];
    identifier_column?: string;
    // Index into `headers` of the column flagged as identifier on the BE
    // (set via "Set Identifier" UI). Construction-platform uses this column
    // as the join key when matching detected elements to schedule rows.
    identifier_col_index?: number | null;
  };
};

export type TakeoffBoundingBox = {
  id?: string;
  type: "rectangle" | "polygon";
  points: Array<{ x: number; y: number }>;
};

/** Matches the construction-platform SelectedArea Pydantic model. */
export type TakeoffSelectedArea = {
  points: Array<{ x: number; y: number }>;
};

export type TradeScopeSelectionResult = {
  selectedPageIds: number[];
  trade: string | null;
  scope: string | null;
  tableData: ScheduleTableData[] | null;
};
